import { initializeApp, FirebaseApp, FirebaseOptions } from 'firebase/app';
import { Auth, getAuth, connectAuthEmulator } from 'firebase/auth';
import {
    Firestore,
    getFirestore,
    connectFirestoreEmulator,
} from 'firebase/firestore';
import {
    Functions,
    getFunctions,
    connectFunctionsEmulator,
} from 'firebase/functions';
import {
    Analytics,
    getAnalytics,
    isSupported as isAnalyticsSupported,
} from 'firebase/analytics';
import { FirebaseStorage, getStorage } from 'firebase/storage';
import { getPerformance } from 'firebase/performance';

// --- Helper to check if running on the client ---
const isClient = typeof window !== 'undefined';

// --- Firebase Config Fetching ---
/**
 * Fetches dynamic Firebase configuration.
 * In development, it tries the Next.js API proxy first, then the direct Firebase Hosting path.
 * In production on Vercel, it uses environment variables.
 * @returns {Promise<FirebaseOptions | null>} The Firebase config object or null if fetch fails.
 */
async function fetchDynamicConfig(): Promise<FirebaseOptions | null> {
    const isDevelopment = process.env.NODE_ENV === 'development';

    // In production (Vercel), use environment variables
    if (!isDevelopment) {
        const config: FirebaseOptions = {
            apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY!,
            authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN!,
            projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID!,
            storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET!,
            messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID!,
            appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID!,
            measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
        };

        // Validate required fields
        const requiredKeys: Array<keyof FirebaseOptions> = [
            'apiKey',
            'authDomain',
            'projectId',
            'appId',
        ];
        const missingKeys = requiredKeys.filter((key) => !config[key]);
        if (missingKeys.length > 0) {
            
            if (isClient) {
                // Map camelCase to proper env var names
                const envVarNames = missingKeys.map(key => {
                    const envVarMap: Record<string, string> = {
                        'apiKey': 'NEXT_PUBLIC_FIREBASE_API_KEY',
                        'authDomain': 'NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN',
                        'projectId': 'NEXT_PUBLIC_FIREBASE_PROJECT_ID',
                        'appId': 'NEXT_PUBLIC_FIREBASE_APP_ID',
                        'storageBucket': 'NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET',
                        'messagingSenderId': 'NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID',
                        'measurementId': 'NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID'
                    };
                    return envVarMap[key] || `NEXT_PUBLIC_FIREBASE_${key.toUpperCase()}`;
                });
                console.error(
                    `[FirebaseCore] Missing required environment variables: ${envVarNames.join(', ')}`
                );
            }
            return null;
        }

        if (isClient) {
            console.log('[FirebaseCore] Using Firebase config from environment variables');
        }
        return config;
    }

    // Development mode: try API routes and Firebase Hosting
    const localApiProxyPath = '/api/firebase-config'; // Our Next.js proxy for emulators
    const firebaseHostingPath = '/__/firebase/init.json'; // Standard Firebase Hosting path

    const urlsToTry: string[] = [localApiProxyPath, firebaseHostingPath];

    for (const url of urlsToTry) {
        try {
            if (isClient)
                console.log(
                    `[FirebaseCore] Attempting to fetch dynamic config from: ${url}`
                );
            const response = await fetch(url);
            if (response.ok) {
                const config = await response.json();
                if (isClient)
                    console.log(
                        `[FirebaseCore] Successfully fetched dynamic config from ${url}:`,
                        config.projectId
                    );
                return config;
            } else {
                if (isClient)
                    console.warn(
                        `[FirebaseCore] Failed to fetch from ${url} (Status: ${response.status}). Trying next if available.`
                    );
            }
        } catch (error) {
            if (isClient)
                console.warn(
                    `[FirebaseCore] Error fetching from ${url}:`,
                    (error as Error).message
                );
        }
    }

    if (isClient)
        console.error(
            '[FirebaseCore] All attempts to fetch dynamic Firebase config failed.'
        );
    return null;
}

// --- Firebase Core Services Initialization ---
interface FirebaseServices {
    app: FirebaseApp | null;
    auth: Auth | null;
    db: Firestore | null;
    functions: Functions | null;
    analytics: Analytics | null;
    storage: FirebaseStorage | null;
    performance: ReturnType<typeof getPerformance> | null;
    config: FirebaseOptions | null | undefined;
}

// Singleton pattern for Firebase services
let firebaseServicesCache: FirebaseServices | null = null;
let initializationPromise: Promise<FirebaseServices> | null = null;

/**
 * Initializes Firebase services (App, Auth, Firestore, Functions, Analytics, Storage) using dynamically fetched configuration.
 * Safe for both server and client environments - returns null services on server.
 * @returns {Promise<FirebaseServices>} A promise that resolves with the initialized Firebase services.
 */
export function initializeFirebaseCore(): Promise<FirebaseServices> {
    if (!isClient) {
        console.log(
            '[FirebaseCore] Server-side context detected. Returning null services.'
        );
        return Promise.resolve({
            app: null,
            auth: null,
            db: null,
            functions: null,
            analytics: null,
            storage: null,
            performance: null,
            config: null,
        });
    }

    if (firebaseServicesCache) {
        return Promise.resolve(firebaseServicesCache);
    }

    if (initializationPromise) {
        return initializationPromise;
    }

    console.log(
        '[FirebaseCore] Starting Firebase core initialization (client)..'
    );

    initializationPromise = (async () => {
        try {
            const dynamicConfig = await fetchDynamicConfig();

            const requiredKeys: Array<keyof FirebaseOptions> = [
                'apiKey',
                'authDomain',
                'projectId',
                'appId',
            ];
            const coreKeysForFullConfig: Array<keyof FirebaseOptions> = [
                'apiKey',
                'authDomain',
                'projectId',
                'storageBucket',
                'messagingSenderId',
                'appId',
            ];
            let missingKeys: string[] = [];
            if (dynamicConfig) {
                missingKeys = requiredKeys.filter(
                    (key) => !(key in dynamicConfig) || !dynamicConfig[key]
                );
            }

            if (!dynamicConfig || missingKeys.length > 0) {
                console.error(
                    `[FirebaseCore] Invalid dynamic config fetched. Missing: ${missingKeys.join(', ')}. Config:`,
                    dynamicConfig
                );
                return {
                    app: null,
                    auth: null,
                    db: null,
                    functions: null,
                    analytics: null,
                    storage: null,
                    performance: null,
                    config: null,
                };
            }

            console.log(
                `[FirebaseCore] Successfully fetched dynamic Firebase config. Keys present: (${
                    Object.keys(dynamicConfig).length
                }) ${JSON.stringify(Object.keys(dynamicConfig))}`
            );

            console.log(
                '[FirebaseCore] Attempting to initialize or get Firebase app with fetched dynamic config'
            );

            let app: FirebaseApp;
            try {
                const appsModule = await import('firebase/app');
                const apps = appsModule.getApps();
                const existingApp = apps.find((a) => a.name === '[DEFAULT]');

                if (existingApp) {
                    app = existingApp;
                    console.warn(
                        '[FirebaseCore] WARNING: Using existing Firebase app instance. This is unexpected if initializeFirebaseCore is meant to be the sole initializer.'
                    );
                } else {
                    app = appsModule.initializeApp(dynamicConfig);
                    console.log(
                        '[FirebaseCore] Firebase app initialized successfully with dynamic config.'
                    );
                }
            } catch (appError) {
                console.error(
                    '[FirebaseCore] Error initializing Firebase app:',
                    appError
                );
                throw appError;
            }

            const auth = getAuth(app);
            const db = getFirestore(app);
            const functions = getFunctions(app);
            const storage = getStorage(app);

            let analytics: Analytics | null = null;
            if (isClient && dynamicConfig?.measurementId) {
                try {
                    if (await isAnalyticsSupported()) {
                        analytics = getAnalytics(app);
                        console.log(
                            '[FirebaseCore] Firebase Analytics initialized'
                        );
                    } else {
                        console.log(
                            '[FirebaseCore] Firebase Analytics not initialized: Not supported in this browser environment'
                        );
                    }
                } catch (analyticsError) {
                    console.warn(
                        '[FirebaseCore] Firebase Analytics initialization failed:',
                        analyticsError
                    );
                }
            }

            let performance: ReturnType<typeof getPerformance> | null = null;
            if (isClient && process.env.NODE_ENV !== 'development') {
                // Disable Performance in development
                try {
                    performance = getPerformance(app);
                    console.log(
                        '[FirebaseCore] Firebase Performance Monitoring initialized.'
                    );
                } catch (perfError) {
                    console.warn(
                        '[FirebaseCore] Firebase Performance Monitoring initialization failed:',
                        perfError
                    );
                }
            } else if (process.env.NODE_ENV === 'development') {
                console.log(
                    '[FirebaseCore] Firebase Performance Monitoring disabled in development mode.'
                );
            }

            if (process.env.NODE_ENV === 'development') {
                console.log(
                    '[FirebaseCore] Development mode: Connecting to emulators...'
                );
                try {
                    connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
                        disableWarnings: true,
                    });
                    console.log(
                        '[FirebaseCore] Connected to Auth emulator on port 9099'
                    );
                    connectFirestoreEmulator(db, '127.0.0.1', 8081);
                    console.log(
                        '[FirebaseCore] Connected to Firestore emulator on port 8081'
                    );
                    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
                    console.log(
                        '[FirebaseCore] Connected to Functions emulator on port 5001'
                    );

                    // Connect to Storage emulator
                    const { connectStorageEmulator } = await import(
                        'firebase/storage'
                    );
                    connectStorageEmulator(storage, '127.0.0.1', 9199);
                    console.log(
                        '[FirebaseCore] Connected to Storage emulator on port 9199'
                    );
                } catch (emulatorError) {
                    console.error(
                        '[FirebaseCore] Error connecting to emulators:',
                        emulatorError
                    );
                }
            }

            const services: FirebaseServices = {
                app,
                auth,
                db,
                functions,
                analytics,
                storage,
                performance,
                config: dynamicConfig,
            };

            firebaseServicesCache = services;
            console.log('[FirebaseCore] Firebase initialization complete');

            return services;
        } catch (error) {
            console.error(
                '[FirebaseCore] CRITICAL error during Firebase initialization:',
                error
            );
            initializationPromise = null;
            return {
                app: null,
                auth: null,
                db: null,
                functions: null,
                analytics: null,
                storage: null,
                performance: null,
                config: null,
            };
        }
    })();

    return initializationPromise;
}

export type { FirebaseOptions, FirebaseServices };
